import {
  CommonModule,
} from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import {
  ColumnMode,
  DatatableComponent as NgxDatatableComponent,
  NgxDatatableModule,
  TableColumn,
} from "@swimlane/ngx-datatable";
import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { ScreenResizeService } from "src/utils/screen-resize.service";

@Component({
    selector: "app-datatable",
    templateUrl: "./datatable.component.html",
    styleUrls: ["./datatable.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, NgxDatatableModule],
})
export class DatatableComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild("tableSummaryOrder", { static: true })
  tableSummaryOrder: TemplateRef<any>;

  @ViewChild("table", { read: ElementRef })
  tableElement?: ElementRef<HTMLElement>;

  @Input() mode = ColumnMode.force;
  @Input() limit = 20;
  @Input() externalSorting = true;
  @Input() enableSummary = false;
  @Input() showOrder = true;
  @Input() summaryPosition = "bottom";
  @Input() maxVisibleRows = 15;

  @Output() pageChange = new EventEmitter();
  @Output() sortChange = new EventEmitter();
  @Output() limitChange = new EventEmitter();

  @ViewChild("table")
  private ngxTable?: NgxDatatableComponent;

  public ColumnMode = ColumnMode;

  subscriptions: Subscription[] = [];
  columnsForDisplay: TableColumn[] = [];
  horizontalScroll = false;
  hasHorizontalOverflow = false;
  hasVerticalOverflow = false;
  horizontalScrollThumbWidthPercent = 100;
  horizontalScrollThumbTranslatePx = 0;
  verticalScrollThumbHeightPercent = 100;
  verticalScrollThumbTranslatePx = 0;
  verticalScrollRailTopPx = 0;
  verticalScrollRailHeightPx = 0;

  form: FormGroup;

  sources: any = [];

  loading: any;

  screenSize!: string;
  showMobileFooter = true;
  readonly loadingSkeletonRows = Array.from({ length: 5 }, (_, index) => index);

  options = [
    { value: 5 },
    { value: 10 },
    { value: 15 },
    { value: 20 },
    { value: 25 },
    { value: 50 },
    { value: 100 },
  ];

  private readonly defaultColumnWidth = 150;
  private readonly horizontalOverflowTolerance = 1;
  private readonly orderColumnProps = new Set(["order", "index"]);
  private readonly actionColumnProps = new Set(["action", "actions", "option"]);
  private sourceColumns: unknown[] = [];
  private columnsContentWidth = 0;
  private bodyElement?: HTMLElement;
  private observedRows: HTMLElement[] = [];
  private resizeObserver?: ResizeObserver;
  private bodyScrollElement?: HTMLElement;
  private bodyScrollListener?: () => void;
  private measurementTimers = new Set<ReturnType<typeof setTimeout>>();
  private recalculateFrame?: number;
  private recalculateTimer?: ReturnType<typeof setTimeout>;
  private measurementRetryCount = 0;
  private readonly maxMeasurementRetries = 5;
  private readonly layoutRefreshDelays = [0, 50, 150];
  private externalLoading = false;

  private scrRezService = inject(ScreenResizeService);
  private fb = inject(FormBuilder);

  constructor() {
    const initialScreenSize = localStorage.getItem("initScrSize");
    this.screenSize =
      initialScreenSize !== null ? JSON.parse(initialScreenSize) : "";
    setTimeout(() => {
      this.onScreenResize();
    }, 300);
  }

  @Input() set datasource(value: any) {
    if (value?.data !== undefined) {
      value.data.forEach((r: any, i: number) => {
        r.order = value.offset + (i + 1);
      });
      this.sources = value;
      if (!this.externalLoading) {
        this.loading = false;
      }
      this.refreshLayout();
    }
  }

  @Input() set isLoading(value: boolean) {
    this.externalLoading = value;
    this.loading = value;
  }

  @Input() set columns(value: unknown[]) {
    this.sourceColumns = Array.isArray(value) ? value : [];
    this.refreshColumns();
    this.refreshLayout();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      limit: new FormControl(this.limit),
    });

    this.subscriptions.push(
      this.scrRezService.onResize$.pipe(debounceTime(250)).subscribe((res) => {
        this.screenSize = res;
        this.onScreenResize();
      }),
      this.form.valueChanges.subscribe((value) => {
        this.limitChange.emit(value);
      })
    );

    this.refreshColumns();
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        this.refreshLayout();
      });

      if (this.tableElement) {
        this.resizeObserver.observe(this.tableElement.nativeElement);
      }
    }

    this.refreshLayout();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((i) => i.unsubscribe());
    this.resizeObserver?.disconnect();

    this.measurementTimers.forEach((timer) => clearTimeout(timer));
    this.measurementTimers.clear();

    if (this.recalculateFrame !== undefined && typeof cancelAnimationFrame !== "undefined") {
      cancelAnimationFrame(this.recalculateFrame);
    }

    if (this.recalculateTimer) {
      clearTimeout(this.recalculateTimer);
    }

    this.cleanupBodyScrollListener();
  }

  onPageChange(event: any) {
    this.loading = true;
    this.pageChange.emit({ paging: event });
  }

  onSortChange(event: any) {
    if (this.externalSorting) {
      this.loading = true;
    }

    this.sortChange.emit({ sort: event });
  }

  shouldShowEmptyRow(): boolean {
    return this.loading !== true && (this.sources.data?.length ?? 0) === 0;
  }

  shouldShowLoadingState(): boolean {
    return this.loading === true && (this.sources.data?.length ?? 0) === 0;
  }

  showHorizontalScrollAffordance(): boolean {
    return this.hasHorizontalOverflow && (this.sources.data?.length ?? 0) > 0;
  }

  showVerticalScrollAffordance(): boolean {
    return this.hasVerticalOverflow && (this.sources.data?.length ?? 0) > 0;
  }

  horizontalScrollThumbTransform(): string {
    return `translateX(${this.horizontalScrollThumbTranslatePx}px)`;
  }

  verticalScrollThumbTransform(): string {
    return `translateY(${this.verticalScrollThumbTranslatePx}px)`;
  }

  onScreenResize() {
    if (["xs", "sm"].includes(this.screenSize)) {
      this.showMobileFooter = this.sources.total <= this.sources.limit;
    } else {
      this.showMobileFooter = true;
    }

    this.refreshLayout();
  }

  refreshLayout(): void {
    this.measurementRetryCount = 0;
    this.layoutRefreshDelays.forEach((delay) => this.scheduleBodyMeasurement(delay));
  }

  private refreshColumns(): void {
    this.columnsForDisplay = this.normalizeColumns(this.sourceColumns);
    this.columnsContentWidth = this.columnsForDisplay.reduce(
      (total, column) => total + this.getColumnWidth(column),
      0
    );
  }

  private getColumnWidth(column: TableColumn): number {
    return typeof column.width === "number" && Number.isFinite(column.width)
      ? column.width
      : this.defaultColumnWidth;
  }

  private normalizeColumns(columns: unknown[]): TableColumn[] {
    const normalizedColumns = columns
      .filter((column): column is TableColumn => this.isTableColumn(column))
      .map((column) => this.lockColumnMovement(column));

    if (
      this.showOrder &&
      !normalizedColumns.some((column) => this.isOrderColumn(column))
    ) {
      normalizedColumns.unshift({
        prop: "order",
        name: "#",
        sortable: false,
        draggable: false,
        resizeable: false,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
        cellClass: "text-center",
        headerClass: "d-flex justify-content-center",
        ...(this.enableSummary
          ? { summaryTemplate: this.tableSummaryOrder }
          : {}),
      });
    }

    const orderColumnIndex = normalizedColumns.findIndex((column) =>
      this.isOrderColumn(column)
    );

    return normalizedColumns.map((column, index) => {
      if (
        Object.prototype.hasOwnProperty.call(column, "frozenLeft") ||
        Object.prototype.hasOwnProperty.call(column, "frozenRight")
      ) {
        return column;
      }

      if (this.isActionColumn(column)) {
        return { ...column, frozenRight: true };
      }

      if (orderColumnIndex >= 0 && index <= orderColumnIndex) {
        return { ...column, frozenLeft: true };
      }

      return column;
    });
  }

  private isOrderColumn(column: TableColumn): boolean {
    return (
      typeof column.prop === "string" && this.orderColumnProps.has(column.prop)
    );
  }

  private isActionColumn(column: TableColumn): boolean {
    return (
      typeof column.prop === "string" && this.actionColumnProps.has(column.prop)
    );
  }

  private isTableColumn(column: unknown): column is TableColumn {
    return typeof column === "object" && column !== null;
  }

  private scheduleBodyMeasurement(delay = 0): void {
    if (!this.tableElement) {
      return;
    }

    const timer = setTimeout(() => {
      this.measurementTimers.delete(timer);
      this.updateBodyViewport();
    }, delay);

    this.measurementTimers.add(timer);
  }

  private updateBodyViewport(): void {
    const table = this.tableElement?.nativeElement;
    const body = table?.querySelector<HTMLElement>(".datatable-body");

    if (!table) {
      return;
    }

    const tableMeasured = this.updateHorizontalScroll(table, body);

    if (tableMeasured) {
      this.scheduleNgxTableRecalculation();
    }

    if (!body) {
      this.hasVerticalOverflow = false;
      this.verticalScrollThumbHeightPercent = 100;
      this.verticalScrollThumbTranslatePx = 0;
      this.verticalScrollRailTopPx = 0;
      this.verticalScrollRailHeightPx = 0;
      this.cleanupBodyScrollListener();
      return;
    }

    const rows = Array.from(
      body.querySelectorAll<HTMLElement>(".datatable-row-wrapper")
    );
    const maxVisibleRows = this.getMaxVisibleRows();
    const visibleRows = rows.slice(0, maxVisibleRows);
    const shouldScroll =
      (this.sources.data?.length ?? rows.length) > maxVisibleRows &&
      visibleRows.length === maxVisibleRows;
    const rowsHeight = visibleRows.reduce(
      (height, row) =>
        height + (row.getBoundingClientRect().height || row.offsetHeight),
      0
    );

    if (shouldScroll && rowsHeight > 0) {
      const horizontalScrollbarHeight = Math.max(
        0,
        body.offsetHeight - body.clientHeight
      );

      table.classList.add("shared-datatable--scrollable");
      table.style.setProperty(
        "--shared-datatable-body-max-height",
        `${Math.ceil(rowsHeight + horizontalScrollbarHeight)}px`
      );
    } else {
      table.classList.remove("shared-datatable--scrollable");
      table.style.removeProperty("--shared-datatable-body-max-height");
    }

    this.updateVerticalScrollAffordance(body);
    this.syncBodyScroll(body);
    this.updateHorizontalScrollAffordance(body);
    this.observeBodyRows(body, visibleRows);
  }

  private getMaxVisibleRows(): number {
    const normalizedRows = Number(this.maxVisibleRows);

    return Number.isFinite(normalizedRows) && normalizedRows > 0
      ? Math.floor(normalizedRows)
      : 15;
  }

  private lockColumnMovement(column: TableColumn): TableColumn {
    return {
      ...column,
      draggable: false,
      resizeable: false,
    };
  }

  private updateHorizontalScroll(table: HTMLElement, body?: HTMLElement | null): boolean {
    const tableWidth =
      table.getBoundingClientRect().width || table.clientWidth;

    if (tableWidth <= 0) {
      if (this.measurementRetryCount < this.maxMeasurementRetries) {
        this.measurementRetryCount += 1;
        this.scheduleBodyMeasurement(50);
      }
      return false;
    }

    this.measurementRetryCount = 0;
    const shouldScroll =
      this.columnsContentWidth > tableWidth ||
      this.hasBodyHorizontalOverflow(body);

    this.hasHorizontalOverflow = shouldScroll;

    if (shouldScroll !== this.horizontalScroll) {
      this.horizontalScroll = shouldScroll;
    }

    return true;
  }

  private hasBodyHorizontalOverflow(body?: HTMLElement | null): boolean {
    if (!body) {
      return false;
    }

    return body.scrollWidth > body.clientWidth + this.horizontalOverflowTolerance;
  }

  private updateHorizontalScrollAffordance(body: HTMLElement): void {
    if (!this.hasHorizontalOverflow || (this.sources.data?.length ?? 0) === 0) {
      this.horizontalScrollThumbWidthPercent = 100;
      this.horizontalScrollThumbTranslatePx = 0;
      return;
    }

    const clientWidth = body.clientWidth || body.offsetWidth;
    const scrollWidth = Math.max(body.scrollWidth, clientWidth);

    if (clientWidth <= 0 || scrollWidth <= clientWidth) {
      this.horizontalScrollThumbWidthPercent = 100;
      this.horizontalScrollThumbTranslatePx = 0;
      return;
    }

    const widthPercent = Math.min(
      100,
      Math.max(12, (clientWidth / scrollWidth) * 100)
    );
    const thumbWidth = (clientWidth * widthPercent) / 100;
    const maxScrollLeft = scrollWidth - clientWidth;
    const maxTranslate = Math.max(0, clientWidth - thumbWidth);
    const scrollRatio = maxScrollLeft > 0 ? body.scrollLeft / maxScrollLeft : 0;

    this.horizontalScrollThumbWidthPercent = widthPercent;
    this.horizontalScrollThumbTranslatePx = Math.round(maxTranslate * scrollRatio);
  }

  private updateVerticalScrollAffordance(body: HTMLElement): void {
    const clientHeight = body.clientHeight || body.offsetHeight;
    const scrollHeight = Math.max(body.scrollHeight, clientHeight);
    const hasOverflow =
      scrollHeight > clientHeight + this.horizontalOverflowTolerance;

    this.hasVerticalOverflow = hasOverflow;
    this.verticalScrollRailTopPx = body.offsetTop || 0;
    this.verticalScrollRailHeightPx = clientHeight;

    if (!hasOverflow || (this.sources.data?.length ?? 0) === 0) {
      this.verticalScrollThumbHeightPercent = 100;
      this.verticalScrollThumbTranslatePx = 0;
      return;
    }

    if (clientHeight <= 0 || scrollHeight <= clientHeight) {
      this.verticalScrollThumbHeightPercent = 100;
      this.verticalScrollThumbTranslatePx = 0;
      return;
    }

    const heightPercent = Math.min(
      100,
      Math.max(12, (clientHeight / scrollHeight) * 100)
    );
    const thumbHeight = (clientHeight * heightPercent) / 100;
    const maxScrollTop = scrollHeight - clientHeight;
    const maxTranslate = Math.max(0, clientHeight - thumbHeight);
    const scrollRatio = maxScrollTop > 0 ? body.scrollTop / maxScrollTop : 0;

    this.verticalScrollThumbHeightPercent = heightPercent;
    this.verticalScrollThumbTranslatePx = Math.round(maxTranslate * scrollRatio);
  }

  private scheduleNgxTableRecalculation(): void {
    if (!this.ngxTable) {
      return;
    }

    if (this.recalculateFrame !== undefined || this.recalculateTimer) {
      return;
    }

    const recalculate = () => {
      this.recalculateFrame = undefined;
      this.recalculateTimer = undefined;
      this.ngxTable?.recalculate();
    };

    if (typeof requestAnimationFrame !== "undefined") {
      this.recalculateFrame = requestAnimationFrame(recalculate);
      return;
    }

    this.recalculateTimer = setTimeout(recalculate, 0);
  }

  private syncBodyScroll(body: HTMLElement): void {
    if (!this.horizontalScroll && !this.hasVerticalOverflow) {
      this.cleanupBodyScrollListener();
      this.syncHeaderHorizontalOffset(0, body.scrollTop);
      return;
    }

    if (this.bodyScrollElement !== body) {
      this.cleanupBodyScrollListener();
      this.bodyScrollElement = body;
      this.bodyScrollListener = () => {
        this.syncHeaderHorizontalOffset(body.scrollLeft, body.scrollTop);
        this.updateHorizontalScrollAffordance(body);
        this.updateVerticalScrollAffordance(body);
      };
      body.addEventListener("scroll", this.bodyScrollListener, { passive: true });
    }

    this.syncHeaderHorizontalOffset(body.scrollLeft, body.scrollTop);
  }

  private syncHeaderHorizontalOffset(offsetX: number, offsetY = 0): void {
    const table = this.ngxTable as unknown as {
      onBodyScroll?: (event: { offsetX: number; offsetY: number }) => void;
    } | undefined;

    table?.onBodyScroll?.({ offsetX, offsetY });
  }

  private cleanupBodyScrollListener(): void {
    if (this.bodyScrollElement && this.bodyScrollListener) {
      this.bodyScrollElement.removeEventListener("scroll", this.bodyScrollListener);
    }

    this.bodyScrollElement = undefined;
    this.bodyScrollListener = undefined;
  }

  private observeBodyRows(body: HTMLElement, rows: HTMLElement[]): void {
    if (!this.resizeObserver) {
      return;
    }

    if (this.bodyElement !== body) {
      if (this.bodyElement) {
        this.resizeObserver.unobserve(this.bodyElement);
      }

      this.bodyElement = body;
      this.resizeObserver.observe(body);
    }

    const rowsChanged =
      rows.length !== this.observedRows.length ||
      rows.some((row, index) => row !== this.observedRows[index]);

    if (!rowsChanged) {
      return;
    }

    this.observedRows.forEach((row) => this.resizeObserver?.unobserve(row));
    this.observedRows = rows;
    this.observedRows.forEach((row) => this.resizeObserver?.observe(row));
  }
}
